import { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import {
  RotateCcw, Search, Check, X, AlertTriangle, Package,
  ChevronRight, ChevronLeft, Printer, RefreshCw,
  Trash2, ShoppingBag, Banknote, FileText, ArrowDown, Upload,
  History, Plus,
} from 'lucide-react';
import DrawerTopUpPrompt from '../components/DrawerTopUpPrompt';
import { localTodayStr } from '../lib/dateFormat';
import { toast } from 'sonner';
import UploadQRDialog from '../components/UploadQRDialog';
import { useUnsavedChangesGuard } from '../lib/useUnsavedChangesGuard';
import CalcInput from '../components/CalcInput';
import ReturnDetailModal from '../components/ReturnDetailModal';
import PrintEngine from '../lib/PrintEngine';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const round2 = v => Math.round((parseFloat(v) || 0) * 100) / 100;

const RETURN_REASONS = [
  'Defective / Not Working',
  'Expired Product',
  'Wrong Product Delivered',
  'Damaged Packaging',
  'Customer Changed Mind',
  'Duplicate Order',
  'Other',
];

const CONDITION_OPTIONS = [
  { value: 'sellable', label: 'Sellable', desc: 'Unopened, good condition — can return to shelf', color: 'emerald' },
  { value: 'damaged', label: 'Damaged', desc: 'Packaging damaged, integrity questionable', color: 'amber' },
  { value: 'expired', label: 'Expired', desc: 'Product past expiry date', color: 'red' },
  { value: 'defective', label: 'Defective', desc: 'Product does not work / contaminated', color: 'red' },
];

const STEPS = [
  { num: 1, label: 'Customer & Reason' },
  { num: 2, label: 'Products' },
  { num: 3, label: 'Condition' },
  { num: 4, label: 'Inventory Action' },
  { num: 5, label: 'Refund Amount' },
  { num: 6, label: 'Confirm & Tender' },
];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center justify-between mb-6">
      {STEPS.map((s, i) => (
        <div key={s.num} className="flex items-center">
          <div className={`flex flex-col items-center ${current >= s.num ? '' : 'opacity-40'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
              current === s.num ? 'bg-[#1A4D2E] text-white ring-2 ring-[#1A4D2E]/30' :
              current > s.num ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
            }`}>
              {current > s.num ? <Check size={14} /> : s.num}
            </div>
            <p className="text-[10px] text-slate-500 mt-1 text-center w-16 leading-tight">{s.label}</p>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`flex-1 h-0.5 mx-2 mt-[-14px] ${current > s.num ? 'bg-emerald-400' : 'bg-slate-200'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function ReturnRefundWizard() {
  const { currentBranch, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(null);
  const [uploadQROpen, setUploadQROpen] = useState(false);

  // Double-submit guard — checked synchronously before any state update
  const submittingRef = useRef(false);

  // Manager PIN authorization dialog (shown before final submit)
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [managerPin, setManagerPin] = useState('');
  const [pinError, setPinError] = useState('');
  // Empty-drawer remedy (owner-approved 2026-08-29): hold the server's
  // insufficient_funds payload so the refund can be funded from the Safe
  // in place rather than dead-ending with the customer at the counter.
  const [fundsShort, setFundsShort] = useState(null);

  // Tab: 'new' = wizard, 'history' = past returns
  const [activeTab, setActiveTab] = useState('new');
  const [returnHistory, setReturnHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedRma, setSelectedRma] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!currentBranch) return;
    setHistoryLoading(true);
    try {
      const res = await api.get(`${BACKEND_URL}/api/returns`, {
        params: { branch_id: currentBranch.id, limit: 100 },
      });
      setReturnHistory(res.data.returns || []);
    } catch { /* silent */ }
    setHistoryLoading(false);
  }, [currentBranch]);

  useEffect(() => {
    if (activeTab === 'history') fetchHistory();
  }, [activeTab, fetchHistory]);

  // Step 1
  const [customerName, setCustomerName] = useState('');
  const [customerType, setCustomerType] = useState('walkin');
  const [reason, setReason] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');

  // Pre-fill invoice number when navigated from another page (e.g. Movement History)
  useEffect(() => {
    const inv = searchParams.get('invoice');
    if (inv) setInvoiceNumber(inv);
  }, [searchParams]);

  // Step 2 — products
  const [returnItems, setReturnItems] = useState([]);

  // Unsaved-changes guard — fires when at least one return line has been
  // queued. Provider renders the dialog.
  useUnsavedChangesGuard({
    isDirty: (returnItems || []).length > 0,
    label: 'Return / Refund',
  });
  const [productSearch, setProductSearch] = useState('');
  const [productMatches, setProductMatches] = useState([]);
  const [activeSearchIdx, setActiveSearchIdx] = useState(-1);
  const searchTimer = useRef(null);

  // Steps 3+4 — condition & action per item
  const updateItem = (idx, updates) =>
    setReturnItems(prev => prev.map((it, i) => i === idx ? { ...it, ...updates } : it));

  // Step 5 — refund
  const [refundMethod, setRefundMethod] = useState('full');
  const [customRefund, setCustomRefund] = useState('');
  const [fundSource, setFundSource] = useState('cashier');
  const [fundBalances, setFundBalances] = useState({ cashier: 0, safe: 0 });

  // Invoice lookup — fetch actual sold prices when invoice number is entered.
  // Used to pre-fill custom_unit_price with the real net price after discounts.
  const [invoiceItems, setInvoiceItems] = useState({}); // { product_id: net_per_unit }
  const [invoiceLookupState, setInvoiceLookupState] = useState(''); // '' | 'loading' | 'found' | 'not_found'
  const invoiceLookupTimer = useRef(null);

  useEffect(() => {
    const num = invoiceNumber.trim();
    if (!num) { setInvoiceItems({}); setInvoiceLookupState(''); return; }
    clearTimeout(invoiceLookupTimer.current);
    invoiceLookupTimer.current = setTimeout(async () => {
      setInvoiceLookupState('loading');
      try {
        const res = await api.get(`${BACKEND_URL}/api/invoices/by-number/${encodeURIComponent(num)}`);
        const inv = res.data;
        const byPid = {};
        (inv.items || []).forEach(item => {
          if (item.product_id) {
            // net_per_unit = actual price paid after discount
            const netPerUnit = item.net_per_unit
              ?? (item.quantity > 0 ? round2(item.total / item.quantity) : item.rate);
            byPid[item.product_id] = netPerUnit;
          }
        });
        setInvoiceItems(byPid);
        setInvoiceLookupState('found');
        // Auto-fill custom_unit_price for any return items already in the list
        setReturnItems(prev => prev.map(it => ({
          ...it,
          custom_unit_price: byPid[it.product_id] !== undefined
            ? byPid[it.product_id]
            : it.custom_unit_price,
          _sold_price_hint: byPid[it.product_id],
        })));
      } catch {
        setInvoiceItems({});
        setInvoiceLookupState('not_found');
      }
    }, 500);
    return () => clearTimeout(invoiceLookupTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceNumber]);

  // Effective unit price for an item — custom override first, then catalog preset
  const effectiveUnitPrice = (it) => {
    if (it.custom_unit_price !== null && it.custom_unit_price !== undefined)
      return parseFloat(it.custom_unit_price) || 0;
    return it.refund_price_type === 'wholesale'
      ? (it.prices?.wholesale || it.prices?.retail || 0)
      : (it.prices?.retail || 0);
  };

  const totalRefundSuggestion = returnItems.reduce((s, it) =>
    s + effectiveUnitPrice(it) * it.quantity, 0);

  const refundAmount = refundMethod === 'none' ? 0
    : refundMethod === 'partial' ? (parseFloat(customRefund) || 0)
    : totalRefundSuggestion;

  // ── Product search ─────────────────────────────────────────────────────
  const searchProducts = useCallback((q) => {
    setProductSearch(q);
    setActiveSearchIdx(-1);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q || q.length < 1) { setProductMatches([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await api.get(`${BACKEND_URL}/api/products`, {
          params: { search: q, limit: 8, branch_id: currentBranch?.id }
        });
        setProductMatches(res.data.products || []);
      } catch { setProductMatches([]); }
    }, 200);
  }, [currentBranch?.id]);

  const handleSearchKeyDown = (e) => {
    if (!productMatches.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveSearchIdx(i => Math.min(i + 1, productMatches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveSearchIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && activeSearchIdx >= 0) { e.preventDefault(); addProduct(productMatches[activeSearchIdx]); }
    else if (e.key === 'Escape') { setProductMatches([]); setActiveSearchIdx(-1); }
  };

  const addProduct = (p) => {
    if (returnItems.find(it => it.product_id === p.id)) {
      toast.info(`${p.name} already in list — increase quantity instead`);
    } else {
      const isVet = (p.category || '').toLowerCase() === 'veterinary';
      // Pre-fill custom_unit_price from invoice lookup if available
      const soldPrice = invoiceItems[p.id] !== undefined ? invoiceItems[p.id] : null;
      setReturnItems(prev => [...prev, {
        product_id: p.id,
        product_name: p.name,
        sku: p.sku,
        category: p.category || '',
        unit: p.unit || '',
        quantity: 1,
        condition: '',
        inventory_action: '',  // always blank — determined by condition in Step 3
        cost_price: p.cost_price || 0,
        prices: p.prices || {},
        refund_price_type: 'retail',
        refund_price: p.prices?.retail || 0,
        custom_unit_price: soldPrice,  // null = use catalog preset; number = override
        _sold_price_hint: soldPrice,   // display-only hint showing the actual sold price
        is_vet: isVet,
      }]);
    }
    setProductSearch('');
    setProductMatches([]);
    setActiveSearchIdx(-1);
  };

  // ── Fetch fund balances before step 5 ─────────────────────────────────
  const goToStep5 = async () => {
    try {
      const res = await api.get(`${BACKEND_URL}/api/purchase-orders/fund-balances`, {
        params: { branch_id: currentBranch?.id }
      });
      setFundBalances({ cashier: res.data.cashier || 0, safe: res.data.safe || 0 });
    } catch {}
    setStep(5);
  };

  // ── Validate step transitions ──────────────────────────────────────────
  const canProceed = () => {
    if (step === 1) return reason.trim().length > 0;
    if (step === 2) return returnItems.length > 0;
    if (step === 3) return returnItems.every(it => it.condition !== '');
    if (step === 4) return returnItems.every(it => it.inventory_action !== '');
    if (step === 5) return true;
    return true;
  };

  const nextStep = () => {
    if (!canProceed()) { toast.error('Please complete all required fields before continuing'); return; }
    if (step === 4) { goToStep5(); return; }
    setStep(s => s + 1);
  };

  // ── Submit ─────────────────────────────────────────────────────────────
  const handleSubmit = async (pinValue) => {
    // Synchronous double-submit guard — blocks re-entry even before React re-renders
    if (submittingRef.current) return;
    submittingRef.current = true;

    if (!currentBranch) { submittingRef.current = false; toast.error('Select a branch'); return; }
    // Phase 2C.5 — backend rejects credit-customer returns without invoice_number
    if (customerType === 'credit' && !invoiceNumber.trim()) {
      submittingRef.current = false;
      toast.error('Credit-customer returns require the original Invoice # so the credit applies to the right AR balance.');
      setStep(1);
      return;
    }
    if (refundAmount > 0) {
      const avail = fundSource === 'safe' ? fundBalances.safe : fundBalances.cashier;
      if (refundAmount > avail) {
        submittingRef.current = false;
        toast.error(`Insufficient ${fundSource} balance. Available: ${formatPHP(avail)}`);
        return;
      }
    }
    setSaving(true);
    try {
      // Whitelisted fields only (audit Group B C4): cost is resolved
      // SERVER-SIDE from the invoice snapshot / branch capital. The local
      // cost_price stays in state for the Step-4 preview (estimated) only.
      const items = returnItems.map(it => ({
        product_id: it.product_id,
        product_name: it.product_name,
        sku: it.sku,
        category: it.category,
        unit: it.unit,
        quantity: it.quantity,
        condition: it.condition,
        inventory_action: it.inventory_action,
        refund_price: effectiveUnitPrice(it),  // uses custom override if set, else catalog preset
      }));
      const body = {
        branch_id: currentBranch.id,
        return_date: localTodayStr(),
        customer_name: customerName || 'Walk-in',
        customer_type: customerType,
        reason,
        invoice_number: invoiceNumber,
        notes,
        items,
        refund_method: refundMethod,
        refund_amount: refundAmount,
        fund_source: fundSource,
      };
      if (pinValue) body.manager_pin = pinValue;
      const res = await api.post(`${BACKEND_URL}/api/returns`, body);
      setCompleted(res.data);
      setStep(6);
      toast.success(`Return ${res.data.rma_number} processed!`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // 2026-08-29: `detail` can be a structured object (insufficient_funds,
      // shift_required…). The old code called .toLowerCase() on it and then
      // handed the OBJECT to toast, which rendered "[object Object]" — the
      // cashier saw nothing usable at the exact moment money was refused.
      if (detail && typeof detail === 'object') {
        if (detail.type === 'insufficient_funds') {
          // Offer the Safe top-up in place instead of dead-ending the refund.
          setFundsShort(detail);
          setPinDialogOpen(false);
        } else {
          toast.error(detail.message || 'Failed to process return', { duration: 9000 });
        }
      } else {
        const msg = detail || 'Failed to process return';
        if (e.response?.status === 403 && String(msg).toLowerCase().includes('pin')) {
          setPinError(msg);
          setPinDialogOpen(true);
        } else {
          toast.error(msg, { duration: 9000 });
        }
      }
    } finally {
      setSaving(false);
      submittingRef.current = false;
    }
  };

  // Called when the user clicks "Process Return" — opens PIN dialog first
  const handleProcessClick = () => {
    setPinError('');
    setManagerPin('');
    setPinDialogOpen(true);
  };

  const handlePinConfirm = () => {
    setPinDialogOpen(false);
    handleSubmit(managerPin || undefined);
  };

  const resetWizard = () => {
    setStep(1); setCompleted(null); setUploadQROpen(false);
    setCustomerName(''); setCustomerType('walkin'); setReason(''); setInvoiceNumber(''); setNotes('');
    setReturnItems([]); setRefundMethod('full'); setCustomRefund(''); setFundSource('cashier');
  };

  // Route through PrintEngine (was a hand-rolled Arial popup that bypassed the
  // thermal pipeline entirely — no thermal CSS/font/viewport, so Thermal prints
  // came out thin/fuzzy). returnSlipThermal/FullPage are the shared templates.
  const printReceipt = async (format = 'thermal') => {
    if (!completed) return;
    let businessInfo = {};
    try { businessInfo = (await api.get('/settings/business-info')).data || {}; }
    catch { /* header falls back to defaults */ }
    PrintEngine.print({
      type: 'return_slip',
      format,
      businessInfo,
      docCode: completed.doc_code || '',
      data: {
        return_number: completed.rma_number,
        created_at: completed.return_date,
        original_invoice_number: completed.invoice_number || '',
        customer_name: completed.customer_name,
        reason: completed.reason,
        refund_amount: completed.refund_amount,
        items: (completed.items || []).map(it => ({
          product_name: it.product_name
            + (it.inventory_action === 'shelf' ? '' : ' (pull-out)'),
          quantity: it.quantity,
          unit: it.unit || '',
          rate: it.refund_price,
          total: (parseFloat(it.refund_price) || 0) * (parseFloat(it.quantity) || 0),
        })),
      },
    });
  };

  if (!currentBranch) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400">
        <p>Please select a branch to process returns.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-5 animate-fadeIn p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#1A4D2E] flex items-center justify-center">
            <RotateCcw size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>Return & Refund</h1>
            <p className="text-xs text-slate-500">Process customer stock returns with inventory and refund tracking</p>
          </div>
        </div>
        {/* Tab switcher */}
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          <button
            onClick={() => setActiveTab('new')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'new' ? 'bg-[#1A4D2E] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            data-testid="tab-new-return"
          >
            <Plus size={14} /> New Return
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors border-l border-slate-200 ${activeTab === 'history' ? 'bg-[#1A4D2E] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            data-testid="tab-return-history"
          >
            <History size={14} /> History
          </button>
        </div>
      </div>

      {/* ── HISTORY TAB ─────────────────────────────────────────────────── */}
      {activeTab === 'history' && (
        <Card style={{ border: '1px solid var(--border-default)' }}>
          <CardContent className="p-0">
            {historyLoading ? (
              <div className="py-12 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
                <RefreshCw size={14} className="animate-spin" /> Loading returns...
              </div>
            ) : returnHistory.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
                <Package size={28} className="opacity-40" />
                No returns found for this branch
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b text-xs text-slate-500 uppercase">
                      <th className="text-left px-4 py-3">RMA #</th>
                      <th className="text-left px-4 py-3">Date</th>
                      <th className="text-left px-4 py-3">Customer</th>
                      <th className="text-left px-4 py-3">Reason</th>
                      <th className="text-right px-4 py-3">Refund</th>
                      <th className="text-center px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {returnHistory.map(r => (
                      <tr
                        key={r.id}
                        onClick={() => setSelectedRma(r.rma_number)}
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                        data-testid={`return-history-row-${r.rma_number}`}
                      >
                        <td className="px-4 py-3 font-mono text-xs font-semibold text-[#1A4D2E]">{r.rma_number}</td>
                        <td className="px-4 py-3 text-slate-600">{r.return_date}</td>
                        <td className="px-4 py-3">{r.customer_name || 'Walk-in'}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{r.reason}</td>
                        <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatPHP(r.refund_amount || 0)}</td>
                        <td className="px-4 py-3 text-center">
                          {r.voided
                            ? <Badge className="bg-red-100 text-red-700 text-[10px]">Voided</Badge>
                            : <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Active</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── WIZARD CARD ─────────────────────────────────────────────────── */}
      {activeTab === 'new' && (
        <Card style={{ border: "1px solid var(--border-default)" }}>
          <CardContent className="p-6">
          <StepIndicator current={step} />

          {/* ── STEP 1: Customer & Reason ──────────────────────────────── */}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-base font-semibold text-slate-700" style={{ fontFamily: 'Manrope' }}>Step 1 — Customer & Reason</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Customer Name (optional)</Label>
                  <Input className="mt-1 h-9" value={customerName} onChange={e => setCustomerName(e.target.value)}
                    placeholder="Walk-in or credit customer name" autoFocus />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Customer Type</Label>
                  <Select value={customerType} onValueChange={setCustomerType}>
                    <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="walkin">Walk-in</SelectItem>
                      <SelectItem value="credit">Credit Customer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Return Reason <span className="text-red-500">*</span></Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger className="mt-1 h-9"><SelectValue placeholder="Select reason..." /></SelectTrigger>
                  <SelectContent>
                    {RETURN_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">
                    {customerType === 'credit' ? (
                      <span>Original Invoice # <span className="text-red-500">*required for credit customers</span></span>
                    ) : (
                      <span>Original Invoice # <span className="text-slate-400">(if available)</span></span>
                    )}
                  </Label>
                  <Input
                    className={`mt-1 h-9 ${customerType === 'credit' && !invoiceNumber.trim() ? 'border-red-300' : ''}`}
                    value={invoiceNumber}
                    onChange={e => setInvoiceNumber(e.target.value)}
                    placeholder="e.g. SI-20260222-0001"
                    data-testid="return-invoice-number-input"
                  />
                  {customerType === 'credit' && !invoiceNumber.trim() && (
                    <p className="text-[10px] text-red-600 mt-1">
                      Required: credit returns must reference the original invoice so the credit applies to the correct AR balance.
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Notes</Label>
                  <Input className="mt-1 h-9" value={notes} onChange={e => setNotes(e.target.value)}
                    placeholder="Additional notes..." />
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 2: Products ───────────────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-base font-semibold text-slate-700" style={{ fontFamily: 'Manrope' }}>Step 2 — Products Being Returned</h2>
              <div className="relative">
                <Label className="text-xs text-slate-500">Search & Add Product</Label>
                <div className="relative mt-1">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    className="h-9 pl-8" value={productSearch}
                    onChange={e => searchProducts(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    onBlur={() => setTimeout(() => { setProductMatches([]); setActiveSearchIdx(-1); }, 150)}
                    placeholder="Type product name or SKU..."
                    autoFocus autoComplete="one-time-code" data-lpignore="true"
                  />
                </div>
                {productMatches.length > 0 && (
                  <div className="absolute z-50 w-full mt-0.5 bg-white border border-slate-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                    {productMatches.map((p, idx) => (
                      <button key={p.id} onMouseDown={() => addProduct(p)}
                        className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 transition-colors ${idx === activeSearchIdx ? 'bg-emerald-50 border-l-[3px] border-l-emerald-700' : 'hover:bg-slate-50'}`}>
                        <span className="font-medium text-sm">{p.name}</span>
                        <span className="text-xs text-slate-400 ml-2">{p.sku} · {p.category}</span>
                        <span className="text-xs text-slate-600 ml-2">{formatPHP(p.prices?.retail || 0)}</span>
                        {(p.category||'').toLowerCase() === 'veterinary' && (
                          <Badge className="ml-2 text-[9px] bg-amber-100 text-amber-700">Veterinary</Badge>
                        )}
                      </button>
                    ))}
                    <div className="px-3 py-1 bg-slate-50 border-t text-[10px] text-slate-400 flex gap-3">
                      <span>↑↓ navigate</span><span>Enter to add</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Invoice match banner */}
              {invoiceNumber.trim() && invoiceLookupState === 'found' && (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700" data-testid="invoice-match-banner">
                  <Check size={12} className="shrink-0" />
                  Invoice {invoiceNumber} matched — prices pre-filled with actual sold amounts (after discounts).
                </div>
              )}
              {invoiceNumber.trim() && invoiceLookupState === 'not_found' && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                  <AlertTriangle size={12} className="shrink-0" />
                  Invoice {invoiceNumber} not found — using catalog prices. Edit the unit price below if the item was discounted.
                </div>
              )}

              {returnItems.length > 0 && (
                <div className="space-y-2">
                  {returnItems.map((it, idx) => {
                    const catalogPrice = it.refund_price_type === 'wholesale'
                      ? (it.prices?.wholesale || it.prices?.retail || 0)
                      : (it.prices?.retail || 0);
                    const isCustomized = it.custom_unit_price !== null && it.custom_unit_price !== undefined;
                    const currentUnitPrice = isCustomized ? it.custom_unit_price : catalogPrice;
                    const lineTotal = round2(effectiveUnitPrice(it) * it.quantity);
                    return (
                      <div key={it.product_id} className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2">
                        {/* Header row */}
                        <div className="flex items-center justify-between">
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{it.product_name}</p>
                            <p className="text-[10px] text-slate-400">{it.sku} · {it.category}</p>
                            {it.is_vet && <Badge className="text-[9px] bg-amber-100 text-amber-700 mt-0.5">Veterinary</Badge>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            <p className="text-sm font-bold text-[#1A4D2E]">{formatPHP(lineTotal)}</p>
                            <button onClick={() => setReturnItems(prev => prev.filter((_, i) => i !== idx))}
                              className="text-slate-300 hover:text-red-500"><Trash2 size={13} /></button>
                          </div>
                        </div>

                        {/* Qty + Price controls */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-400 shrink-0">Qty:</span>
                            <CalcInput value={it.quantity}
                              onChange={(v) => updateItem(idx, { quantity: parseInt(v) || 1 })}
                              className="h-7 w-16 text-center" />
                          </div>

                          <Select value={it.refund_price_type || 'retail'}
                            onValueChange={v => updateItem(idx, {
                              refund_price_type: v,
                              custom_unit_price: it._sold_price_hint !== null && it._sold_price_hint !== undefined
                                ? it._sold_price_hint
                                : null,
                            })}>
                            <SelectTrigger className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="retail">@ Retail</SelectItem>
                              <SelectItem value="wholesale">@ Wholesale</SelectItem>
                            </SelectContent>
                          </Select>

                          <div className="flex items-center gap-1 flex-1 min-w-[120px]">
                            <span className="text-xs text-slate-400 shrink-0">Unit price:</span>
                            <CalcInput
                              value={String(currentUnitPrice ?? '')}
                              onChange={v => updateItem(idx, { custom_unit_price: parseFloat(v) || 0 })}
                              className="h-7 text-xs w-24 text-right"
                              data-testid={`refund-unit-price-${idx}`}
                            />
                            {isCustomized && round2(currentUnitPrice) !== round2(catalogPrice) && (
                              <button
                                onClick={() => updateItem(idx, { custom_unit_price: null })}
                                className="text-[10px] text-slate-400 hover:text-slate-600 underline shrink-0"
                                title="Reset to catalog price"
                              >
                                reset
                              </button>
                            )}
                          </div>

                          {it._sold_price_hint !== null && it._sold_price_hint !== undefined &&
                           round2(it._sold_price_hint) !== round2(catalogPrice) && (
                            <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded shrink-0">
                              sold @ {formatPHP(it._sold_price_hint)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex justify-between text-sm font-bold pt-2 border-t">
                    <span>Estimated Total Refund</span>
                    <span className="text-[#1A4D2E]">{formatPHP(totalRefundSuggestion)}</span>
                  </div>
                </div>
              )}
              {returnItems.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <Package size={32} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Search and add products to return</p>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 3: Condition ──────────────────────────────────────── */}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-base font-semibold text-slate-700" style={{ fontFamily: 'Manrope' }}>Step 3 — Condition Assessment</h2>
              <p className="text-sm text-slate-500">Manager inspects each product and determines condition.</p>
              {returnItems.map((it, idx) => (
                <Card key={it.product_id} className={`border-2 ${it.condition ? 'border-slate-200' : 'border-amber-300'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold">{it.product_name}</p>
                        <p className="text-xs text-slate-400">{it.sku} · Qty: {it.quantity} {it.unit}</p>
                      </div>
                      {it.is_vet && <Badge className="bg-amber-100 text-amber-700 text-xs">Veterinary</Badge>}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {CONDITION_OPTIONS.map(opt => {
                        const colors = {
                          emerald: 'border-emerald-400 bg-emerald-50 text-emerald-800',
                          amber: 'border-amber-400 bg-amber-50 text-amber-800',
                          red: 'border-red-400 bg-red-50 text-red-800',
                        };
                        const selected = it.condition === opt.value;
                        return (
                          <button key={opt.value}
                            onClick={() => {
                              const autoAction = opt.value === 'sellable' ? '' : 'pullout';
                              updateItem(idx, { condition: opt.value, inventory_action: autoAction });
                            }}
                            className={`p-2.5 rounded-lg border-2 text-left text-xs transition-all ${selected ? colors[opt.color] : 'border-slate-200 hover:border-slate-300'}`}>
                            <p className="font-semibold">{opt.label}</p>
                            <p className="opacity-70 mt-0.5 text-[10px] leading-tight">{opt.desc}</p>
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* ── STEP 4: Inventory Action ───────────────────────────────── */}
          {step === 4 && (
            <div className="space-y-4">
              <h2 className="text-base font-semibold text-slate-700" style={{ fontFamily: 'Manrope' }}>Step 4 — Inventory Decision</h2>
              <p className="text-sm text-slate-500">Decide what happens to each item's stock based on the condition you assessed in Step 3.</p>
              {returnItems.map((it, idx) => {
                const canReturnToShelf = it.condition === 'sellable';
                // Non-sellable (damaged/expired/defective) always pulled out
                if (!canReturnToShelf && it.inventory_action !== 'pullout') {
                  updateItem(idx, { inventory_action: 'pullout' });
                }
                return (
                  <Card key={it.product_id} className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-semibold">{it.product_name}</p>
                          <p className="text-xs text-slate-400">{it.quantity} {it.unit} · {it.condition}</p>
                        </div>
                        <Badge className={it.condition === 'sellable' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                          {it.condition}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          disabled={!canReturnToShelf}
                          onClick={() => updateItem(idx, { inventory_action: 'shelf' })}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            it.inventory_action === 'shelf'
                              ? 'border-emerald-500 bg-emerald-50'
                              : canReturnToShelf ? 'border-slate-200 hover:border-emerald-300' : 'border-slate-100 opacity-40 cursor-not-allowed bg-slate-50'
                          }`}>
                          <div className="flex items-center gap-2 mb-1">
                            <ShoppingBag size={16} className="text-emerald-600" />
                            <p className="font-semibold text-sm">Return to Shelf</p>
                          </div>
                          <p className="text-xs text-slate-500">Inventory +{it.quantity} {it.unit}</p>
                          {it.is_vet && <p className="text-[10px] text-amber-600 mt-0.5">Veterinary — verify condition first</p>}
                        </button>
                        <button
                          onClick={() => updateItem(idx, { inventory_action: 'pullout' })}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            it.inventory_action === 'pullout'
                              ? 'border-red-500 bg-red-50'
                              : 'border-slate-200 hover:border-red-300'
                          }`}>
                          <div className="flex items-center gap-2 mb-1">
                            <Trash2 size={16} className="text-red-600" />
                            <p className="font-semibold text-sm">Pull Out (Loss)</p>
                          </div>
                          <p className="text-xs text-slate-500">
                            Recorded as loss · est. {formatPHP((it.cost_price || 0) * it.quantity)} capital
                            <span className="text-slate-400"> (final value computed by the system)</span>
                          </p>
                          <p className="text-[10px] text-red-500 mt-0.5">Owner notified · Audit logged</p>
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* ── STEP 5: Refund Amount ──────────────────────────────────── */}
          {step === 5 && (
            <div className="space-y-4">
              <h2 className="text-base font-semibold text-slate-700" style={{ fontFamily: 'Manrope' }}>Step 5 — Refund Amount</h2>

              {/* Total summary */}
              <div className="flex justify-between items-center px-3 py-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <span className="text-sm text-slate-600">Total to refund ({returnItems.length} item{returnItems.length !== 1 ? 's' : ''})</span>
                <span className="text-lg font-bold text-[#1A4D2E]">{formatPHP(totalRefundSuggestion)}</span>
              </div>

              {/* Refund method */}
              <div>
                <Label className="text-xs text-slate-500">Refund Method</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {[
                    { v: 'full', label: 'Full Refund', desc: formatPHP(totalRefundSuggestion) },
                    { v: 'partial', label: 'Partial / Custom', desc: 'Enter amount' },
                    { v: 'none', label: 'No Refund', desc: 'Exchange only' },
                  ].map(opt => (
                    <button key={opt.v} onClick={() => setRefundMethod(opt.v)}
                      className={`p-2.5 rounded-lg border-2 text-left text-xs transition-all ${refundMethod === opt.v ? 'border-[#1A4D2E] bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <p className="font-semibold">{opt.label}</p>
                      <p className="text-slate-500 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
                {refundMethod === 'partial' && (
                  <div className="mt-2">
                    <Label className="text-xs text-slate-500">Custom Amount</Label>
                    <CalcInput value={customRefund}
 onChange={(v) => setCustomRefund(v)}
 className="mt-1 h-9 text-lg" placeholder="0.00" autoFocus />
                  </div>
                )}
              </div>

              {/* Fund source */}
              {refundAmount > 0 && (
                <div>
                  <Label className="text-xs text-slate-500">Fund Source</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {[
                      { k: 'cashier', label: 'Cashier', bal: fundBalances.cashier },
                      { k: 'safe', label: 'Safe / Vault', bal: fundBalances.safe },
                    ].map(f => (
                      <button key={f.k} onClick={() => setFundSource(f.k)}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${fundSource === f.k ? 'border-[#1A4D2E] bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                        <p className="text-xs font-medium text-slate-600">{f.label}</p>
                        <p className={`text-lg font-bold  ${f.bal < refundAmount ? 'text-red-600' : 'text-[#1A4D2E]'}`}>
                          {formatPHP(f.bal)}
                        </p>
                        {f.bal < refundAmount && <p className="text-[10px] text-red-500">Insufficient</p>}
                        {f.bal >= refundAmount && fundSource === f.k && (
                          <p className="text-[10px] text-emerald-600">After: {formatPHP(f.bal - refundAmount)}</p>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 6: Confirm & Complete ──────────────────────────────── */}
          {step === 6 && completed && (
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <Check size={28} className="text-emerald-600" />
                </div>
                <h2 className="text-xl font-bold text-emerald-700" style={{ fontFamily: 'Manrope' }}>Return Processed!</h2>
                <p className="text-slate-500 text-sm mt-1">RMA Number: <b>{completed.rma_number}</b></p>
              </div>

              <Card style={{ border: "1px solid var(--border-default)" }}>
                <CardContent className="p-4 space-y-2">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-400">Customer:</span> <b>{completed.customer_name}</b></div>
                    <div><span className="text-slate-400">Reason:</span> {completed.reason}</div>
                    <div><span className="text-slate-400">Date:</span> {completed.return_date}</div>
                    {completed.invoice_number && <div><span className="text-slate-400">Invoice:</span> {completed.invoice_number}</div>}
                  </div>
                  <Separator />
                  {completed.items?.map((it, i) => (
                    <div key={i} className="flex items-center justify-between text-sm py-0.5">
                      <span>{it.product_name} × {it.quantity}</span>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-[9px] ${it.inventory_action === 'shelf' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {it.inventory_action === 'shelf' ? 'Back to Shelf' : 'Pull Out'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  <Separator />
                  <div className="flex justify-between font-bold text-lg">
                    <span>Refund Tendered</span>
                    <span className="text-[#1A4D2E]">{formatPHP(completed.refund_amount)}</span>
                  </div>
                  {completed.has_pullout && (
                    <div className="mt-2 p-2 rounded bg-red-50 border border-red-200 text-xs text-red-700">
                      <AlertTriangle size={12} className="inline mr-1" />
                      Pull-out loss of {formatPHP(completed.total_loss_value)} recorded. Owner notified.
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => printReceipt('thermal')}>
                  <Printer size={14} className="mr-1.5" /> Print Thermal
                </Button>
                <Button variant="outline" onClick={() => printReceipt('dot_matrix')}>
                  <Printer size={14} className="mr-1.5" /> Print Dot Matrix
                </Button>
                <Button variant="outline" onClick={() => setUploadQROpen(true)}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50">
                  <Upload size={14} className="mr-1.5" /> Upload Product Photos
                </Button>
                <Button onClick={resetWizard} className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">
                  <RotateCcw size={14} className="mr-1.5" /> New Return
                </Button>
              </div>
              <UploadQRDialog
                open={uploadQROpen}
                onClose={(count) => { setUploadQROpen(false); if (count > 0) toast.success(`${count} photo(s) saved to return ${completed?.rma_number}`); }}
                recordType="return"
                recordId={completed?.id}
              />
            </div>
          )}

          {/* Empty drawer → fund it from the Safe here (owner 2026-08-29) */}
          {fundsShort && step === 5 && (
            <div className="mt-6">
              <DrawerTopUpPrompt
                detail={fundsShort}
                branchId={currentBranch?.id}
                linkRef={invoiceNumber || ''}
                onFunded={() => { setFundsShort(null); handleProcessClick(); }}
                onCancel={() => setFundsShort(null)}
              />
            </div>
          )}

          {/* Navigation */}
          {step < 6 && (
            <div className="flex justify-between mt-6 pt-4 border-t border-slate-100">
              <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={step === 1}>
                <ChevronLeft size={14} className="mr-1.5" /> Back
              </Button>
              {step < 5 ? (
                <Button onClick={nextStep} disabled={!canProceed()}
                  className="bg-[#1A4D2E] hover:bg-[#14532d] text-white"
                  data-testid={`step-${step}-next`}>
                  Continue <ChevronRight size={14} className="ml-1.5" />
                </Button>
              ) : (
                <Button onClick={handleProcessClick} disabled={saving}
                  className="bg-[#1A4D2E] hover:bg-[#14532d] text-white"
                  data-testid="process-return-btn">
                  {saving ? <RefreshCw size={14} className="animate-spin mr-1.5" /> : <Check size={14} className="mr-1.5" />}
                  Process Return & Refund
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* ── Manager PIN Authorization Dialog ───────────────────────────────── */}
      {pinDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle size={18} className="text-amber-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-800 text-sm">Authorization Required</p>
                <p className="text-xs text-slate-500">Enter manager PIN to process this return</p>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Manager PIN</Label>
              <Input
                type="password"
                inputMode="numeric"
                placeholder="Enter PIN"
                value={managerPin}
                onChange={e => { setManagerPin(e.target.value); setPinError(''); }}
                onKeyDown={e => e.key === 'Enter' && managerPin && handlePinConfirm()}
                autoFocus
                data-testid="return-manager-pin-input"
                className="text-center tracking-widest text-lg font-mono"
              />
              {pinError && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <X size={11} /> {pinError}
                </p>
              )}
              <p className="text-[10px] text-slate-400">
                Leave blank to submit without PIN (if your team settings don't require it)
              </p>
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" className="flex-1"
                onClick={() => { setPinDialogOpen(false); setPinError(''); }}
                data-testid="return-pin-cancel">
                Cancel
              </Button>
              <Button size="sm"
                className="flex-1 bg-[#1A4D2E] hover:bg-[#14532d] text-white"
                onClick={handlePinConfirm}
                disabled={saving}
                data-testid="return-pin-confirm">
                {saving ? <RefreshCw size={12} className="animate-spin mr-1" /> : <Check size={12} className="mr-1" />}
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Return Detail Modal — used by History tab */}
      <ReturnDetailModal
        rmaNumber={selectedRma}
        open={!!selectedRma}
        onClose={() => setSelectedRma(null)}
        onVoided={() => { setSelectedRma(null); fetchHistory(); }}
      />
    </div>
  );
}
